<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { BetaFileInput } from '@/types/beta-dashboard'
import { betaFileExtension, slugBetaFilePath, validateBetaFile } from '@/utils/beta-files'

const props = defineProps<{
    open: boolean
    siteUrl?: string | null
    uploading?: boolean
    saving?: boolean
}>()

const emit = defineEmits<{
    close: []
    upload: [file: File]
    save: [file: BetaFileInput]
}>()

const name = ref('')
const path = ref('')
const uploadedFile = ref<unknown>(null)
const selectedName = ref('')
const error = ref('')

watch(
    () => props.open,
    (open) => {
        if (!open) return
        name.value = ''
        path.value = ''
        uploadedFile.value = null
        selectedName.value = ''
        error.value = ''
    },
)

const busy = computed(() => Boolean(props.uploading || props.saving))
const canSave = computed(() => Boolean(name.value.trim() && path.value.trim() && uploadedFile.value) && !busy.value)
const previewUrl = computed(() => {
    const base = String(props.siteUrl || '').replace(/\/$/, '')
    return `${base}/downloads/${path.value || 'caminho-do-arquivo'}`
})

const selectFile = (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    const validationError = validateBetaFile(file)
    if (validationError) {
        error.value = validationError
        input.value = ''
        return
    }

    error.value = ''
    uploadedFile.value = null
    selectedName.value = file.name
    if (!name.value) {
        name.value = file.name
        path.value = slugBetaFilePath(file.name)
    } else {
        const extension = betaFileExtension(file.name)
        path.value = slugBetaFilePath(`${path.value}.${extension}`)
    }
    emit('upload', file)
}

const applyUploadedFile = (value: unknown) => {
    uploadedFile.value = value
}

const changeName = (value: string) => {
    name.value = value
    if (!uploadedFile.value) path.value = slugBetaFilePath(value)
}

const changePath = (value: string) => {
    path.value = slugBetaFilePath(value)
}

const save = () => {
    if (!canSave.value) return

    emit('save', {
        id: null,
        file: uploadedFile.value,
        name: name.value.trim(),
        path: slugBetaFilePath(path.value),
    })
}

defineExpose({ applyUploadedFile })
</script>

<template>
    <Teleport to="body">
        <div v-if="open" class="beta-file-dialog" role="dialog" aria-modal="true">
            <button type="button" class="beta-file-dialog__backdrop" aria-label="Fechar" @click="emit('close')"></button>

            <form class="beta-file-dialog__panel" @submit.prevent="save">
                <header>
                    <div>
                        <p>Arquivos</p>
                        <h2>Adicionar novo arquivo</h2>
                    </div>
                    <button type="button" class="is-close" aria-label="Fechar" @click="emit('close')">×</button>
                </header>

                <div class="beta-file-dialog__body">
                    <label class="beta-file-dialog__field">
                        <span>Nome do arquivo</span>
                        <input :value="name" type="text" maxlength="180" @input="changeName(($event.target as HTMLInputElement).value)">
                    </label>

                    <label class="beta-file-dialog__field">
                        <span>Caminho</span>
                        <input :value="path" type="text" maxlength="180" @change="changePath(($event.target as HTMLInputElement).value)">
                    </label>

                    <label class="beta-file-dialog__upload">
                        <input type="file" :disabled="busy" @change="selectFile">
                        <span class="beta-file-dialog__upload-icon" aria-hidden="true">↑</span>
                        <strong>{{ uploading ? 'Enviando arquivo…' : selectedName || 'Selecionar arquivo' }}</strong>
                        <small>Até 30 MB · executáveis e arquivos de banco não são permitidos</small>
                    </label>

                    <p v-if="uploadedFile" class="beta-file-dialog__uploaded">Arquivo enviado e pronto para cadastrar.</p>
                    <p v-if="error" class="beta-file-dialog__error">{{ error }}</p>

                    <aside class="beta-file-dialog__preview">
                        <span>URL final</span>
                        <strong>{{ previewUrl }}</strong>
                    </aside>
                </div>

                <footer>
                    <button type="button" class="is-cancel" :disabled="busy" @click="emit('close')">Cancelar</button>
                    <button type="submit" class="is-save" :disabled="!canSave">
                        {{ saving ? 'Adicionando…' : 'Adicionar arquivo' }}
                    </button>
                </footer>
            </form>
        </div>
    </Teleport>
</template>

<style scoped>
.beta-file-dialog {
    position: fixed;
    inset: 0;
    z-index: 110;
    display: grid;
    place-items: center;
    padding: 18px;
}

.beta-file-dialog__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(20, 35, 57, 0.64);
    backdrop-filter: blur(7px);
}

.beta-file-dialog__panel {
    position: relative;
    display: grid;
    width: min(560px, 100%);
    overflow: hidden;
    border-radius: 22px;
    background: #fff;
}

.beta-file-dialog__panel > header,
.beta-file-dialog__panel > footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
}

.beta-file-dialog__panel > header {
    border-bottom: 1px solid #e8edf3;
}

.beta-file-dialog__panel > header p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.beta-file-dialog__panel > header h2 {
    margin-top: 2px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 700;
}

.beta-file-dialog__panel .is-close {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #f0f4f9;
    color: #60718a;
    font-size: 18px;
}

.beta-file-dialog__body {
    display: grid;
    gap: 12px;
    padding: 15px 18px;
}

.beta-file-dialog__field {
    display: grid;
    gap: 6px;
}

.beta-file-dialog__field > span,
.beta-file-dialog__preview > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 700;
}

.beta-file-dialog__field input {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 12px;
    background: #fff;
    padding: 10px 11px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-file-dialog__field input:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-file-dialog__upload {
    position: relative;
    display: grid;
    min-height: 112px;
    place-content: center;
    justify-items: center;
    gap: 5px;
    border: 1px dashed color-mix(in srgb, var(--main-color) 35%, #dce4ef);
    border-radius: 14px;
    background: #f7f9fc;
    color: var(--main-color);
    text-align: center;
    cursor: pointer;
}

.beta-file-dialog__upload input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
}

.beta-file-dialog__upload-icon {
    font-size: 22px;
    font-weight: 750;
}

.beta-file-dialog__upload strong {
    font-size: 12px;
    font-weight: 700;
}

.beta-file-dialog__upload small {
    color: #8493a7;
    font-size: 10px;
}

.beta-file-dialog__uploaded {
    color: #2f8a5b;
    font-size: 10px;
    font-weight: 750;
}

.beta-file-dialog__error {
    color: #b24c4c;
    font-size: 10px;
    font-weight: 750;
}

.beta-file-dialog__preview {
    display: grid;
    gap: 4px;
    border-radius: 12px;
    background: #f1f4f8;
    padding: 10px 11px;
}

.beta-file-dialog__preview strong {
    overflow: hidden;
    color: #718299;
    font-size: 12px;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.beta-file-dialog__panel > footer {
    justify-content: flex-end;
    border-top: 1px solid #e8edf3;
    background: #fbfcfe;
}

.beta-file-dialog__panel > footer button {
    min-height: 38px;
    border-radius: var(--beta-button-radius);
    padding: 0 15px;
    font-size: 10px;
    font-weight: 700;
}

.beta-file-dialog__panel > footer .is-cancel {
    color: #687b94;
}

.beta-file-dialog__panel > footer .is-save {
    background: var(--main-color);
    color: #fff;
}

.beta-file-dialog__panel button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

@media (max-width: 520px) {
    .beta-file-dialog {
        place-items: end center;
        padding: 0;
    }

    .beta-file-dialog__panel {
        border-radius: 22px 22px 0 0;
    }
}
</style>
